import React, { useState } from 'react';
import { StyleSheet, Text, View, Modal, TextInput, Dimensions } from 'react-native';
import GenericButton from '../molecules/GenericButton';
import { useThemeContext } from '../../contexts';
import { getResponsivePadding, getResponsiveFontSize } from '../../helpers/utils';

const { width, height } = Dimensions.get('window');

interface RejectOfferModal {
  modalVisible: boolean;
  setModalVisible: (modalVisible: boolean) => void;
  rejectAction: () => void;
}

const RejectOfferModal: React.FC<RejectOfferModal> = ({
  modalVisible,
  setModalVisible,
  rejectAction
}) => {
  const minReasonLength = 1;
  const [text, setText] = useState('');
  const [isValid, setIsValid] = useState(true);
  const { theme } = useThemeContext();

  const styles = createStyles(theme);

  const confirmRejection = async () => {
    if (text.length > minReasonLength) {
      setIsValid(true);
      setModalVisible(false);
      rejectAction();
      setText('');
    } else {
      setIsValid(false);
    }
  };

  return (
    <Modal
      visible={modalVisible}
      transparent={true}
      onRequestClose={() => setModalVisible(false)}
      animationType="fade">
      <View style={styles.modalContainer}>
        <View style={styles.modalContent}>
          <View style={styles.contentSection}>
            <Text style={styles.modalHeaderText}>Reject offer?</Text>
            <Text style={styles.modalText}>Provide a reason for rejecting this offer:</Text>
            <View style={styles.inputContainer}>
              <TextInput
                style={[styles.input, !isValid && styles.inputError]}
                value={text}
                onChangeText={(newText) => {
                  setText(newText);
                  if (!isValid && newText.length >= minReasonLength) setIsValid(true);
                }}
                onSubmitEditing={confirmRejection}
                multiline={true}
                scrollEnabled={true}
                textAlignVertical="top"
                placeholder="Enter reason for rejection..."
              />
              {!isValid && (
                <Text style={styles.errorText}>Must provide a reason for rejection.</Text>
              )}
            </View>
          </View>
          <View style={styles.buttonContainer}>
            <GenericButton
              title="Cancel"
              isPrimary={false}
              onPress={() => {
                setModalVisible(false);
              }}
              buttonStyle={styles.cancelButton}
              textStyle={styles.cancelButtonText}
            />
            <GenericButton
              title="Confirm"
              isPrimary={true}
              onPress={confirmRejection}
              buttonStyle={styles.confirmButton}
              textStyle={styles.confirmButtonText}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default RejectOfferModal;

const createStyles = (theme: any) =>
  StyleSheet.create({
    modalContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      padding: getResponsivePadding(16, 20, 24)
    },
    modalContent: {
      backgroundColor: 'white',
      padding: getResponsivePadding(16, 20, 24),
      borderRadius: 10,
      width: '100%',
      maxWidth: Math.min(width * 0.9, 400),
      minWidth: Math.min(width * 0.8, 320),
      maxHeight: height * 0.7,
      minHeight: 220
    },
    contentSection: {
      flex: 1,
      marginBottom: getResponsivePadding(12, 14, 16)
    },
    modalHeaderText: {
      fontSize: getResponsiveFontSize(18, 20, 24),
      fontWeight: '700',
      marginBottom: getResponsivePadding(8, 10, 12),
      color: '#1C274C'
    },
    modalText: {
      fontSize: getResponsiveFontSize(13, 14, 16),
      fontWeight: '400',
      marginBottom: getResponsivePadding(8, 10, 12),
      color: '#4B5563'
    },
    inputContainer: {
      width: '100%'
    },
    errorText: {
      color: 'red',
      fontSize: getResponsiveFontSize(10, 11, 12),
      fontWeight: '400',
      marginTop: 4
    },
    buttonContainer: {
      flexDirection: width < 400 ? 'column' : 'row',
      justifyContent: 'space-between',
      width: '100%',
      gap: getResponsivePadding(8, 10, 12)
    },
    input: {
      height: 60,
      borderColor: '#666666',
      borderWidth: 1,
      paddingHorizontal: getResponsivePadding(8, 10, 12),
      paddingVertical: getResponsivePadding(8, 10, 12),
      borderRadius: 6,
      color: '#000',
      fontSize: getResponsiveFontSize(13, 14, 16),
      width: '100%'
    },
    inputError: {
      borderColor: 'red',
      borderWidth: 2
    },
    cancelButton: {
      backgroundColor: theme.background,
      minWidth: width < 400 ? '100%' : 140,
      height: 45,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: '#666666',
      flex: width < 400 ? 0 : 1,
      marginRight: width < 400 ? 0 : 8
    },
    confirmButton: {
      backgroundColor: theme.primary,
      minWidth: width < 400 ? '100%' : 140,
      height: 45,
      borderRadius: 6,
      borderColor: 'Black',
      borderWidth: 1,
      flex: width < 400 ? 0 : 1
    },
    cancelButtonText: {
      fontSize: getResponsiveFontSize(14, 15, 18),
      fontWeight: '700',
      color: 'Black'
    },
    confirmButtonText: {
      fontSize: getResponsiveFontSize(14, 15, 18),
      fontWeight: '700',
      color: '#fff'
    }
  });
